import React, { useCallback, useMemo, useState } from "react";
import { Button, Form, InputGroup, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../../Hooks/useDisableScrollOnFocus";
import { toMoneyNumber } from "../../../utils/financeEngine";
import { BillingPharmacyMedicinesTable } from "../../PharmacySales/NewSales/components/BillingPharmacySection";
import {
	BillingInvestigationsAdjustments,
	BillingServicesAdjustments,
} from "../../PharmacySales/NewSales/components/BillingServicesSection";
import { BillingMasterSelect } from "../../PharmacySales/NewSales/components/billingModalParts";
import {
	convertLineAmount,
	formatMasterOptionLabel,
} from "../../PharmacySales/NewSales/utils/billingUtils";
import { formatAmount } from "../utils/patientAccountUtils";
import "../../PharmacySales/NewSales/components/BillingPaymentModal.scss";

const SECTION_ACCENTS = {
	services: "services",
	investigations: "lab",
	medicines: "pharmacy",
};

export const SourceBadge = ({ variant, children }) => (
	<span className={`cp-badge cp-badge--${variant}`}>{children}</span>
);

export const SectionBlock = ({
	accent = "services",
	icon,
	title,
	total,
	showTotal = true,
	currency,
	getCurrencySymbol,
	isOpen,
	onToggle,
	children,
	footer,
	itemCount = 0,
}) => (
	<div
		className={`cp-section cp-section--${SECTION_ACCENTS[accent] || accent} ${
			isOpen ? "cp-section--open" : ""
		}`}
	>
		<button type="button" className="cp-section-head" onClick={onToggle} aria-expanded={isOpen}>
			<span className="cp-section-head-left">
				<span className="cp-section-icon" aria-hidden="true">
					<i className={`fa ${icon}`} />
				</span>
				<span className="cp-section-title">
					{title}
					{itemCount > 0 && (
						<span className="cp-section-count-inline">({itemCount})</span>
					)}
				</span>
			</span>
			<span className="cp-section-head-right">
				{showTotal && total != null && (
					<strong className="cp-section-total">
						{formatAmount(total, currency, getCurrencySymbol)}
					</strong>
				)}
				<i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} aria-hidden="true" />
			</span>
		</button>
		{isOpen && (
			<div className="cp-section-body">
				{children}
				{footer}
			</div>
		)}
	</div>
);

export const SectionDivider = ({ title }) => (
	<div className="cp-section-divider-label">
		<span>{title}</span>
	</div>
);


export const LineAmountInput = ({
	item,
	updateFn,
	disabled,
	displayCurrency,
	convertCurrency,
	pharmacyCurrency,
	allowedCurrencies,
	disableScrollProps,
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState("");

	const value = useMemo(() => {
		if (!item?.amount && item?.amount !== 0) return "";
		return String(
			convertLineAmount(
				item.amount,
				item.currency,
				displayCurrency,
				convertCurrency,
				pharmacyCurrency,
				allowedCurrencies
			)
		);
	}, [allowedCurrencies, convertCurrency, displayCurrency, item, pharmacyCurrency]);

	const commit = (raw) => {
		const displayAmount = toMoneyNumber(raw || 0);
		const nativeAmount = convertLineAmount(
			displayAmount,
			displayCurrency,
			item.currency,
			convertCurrency,
			pharmacyCurrency,
			allowedCurrencies
		);
		updateFn(item.id, nativeAmount);
		setEditing(false);
		setDraft("");
	};

	return (
		<Form.Control
			size="sm"
			type="text"
			className="cp-amount-input"
			disabled={disabled}
			value={editing ? draft : value}
			onFocus={(e) => {
				setEditing(true);
				setDraft(value);
				disableScrollProps?.onFocus?.(e);
			}}
			onChange={(e) => {
				const raw = e.target.value;
				if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) setDraft(raw);
			}}
			onBlur={(e) => {
				commit(e.target.value);
				disableScrollProps?.onBlur?.(e);
			}}
			onKeyDown={disableScrollProps?.onKeyDown}
		/>
	);
};

export const PrescribedMedicinesBlock = ({ prescriptions, t }) => {
	if (!prescriptions?.length) return null;

	return (
		<div className="cp-prescribed-block">
			<div className="cp-block-label">{t("Prescribed Medicines")}</div>
			<p className="cp-block-hint">{t("Doctor prescription — reference for dispensing.")}</p>
			<div className="table-responsive">
				<Table size="sm" className="cp-table cp-table--prescribed mb-0">
					<thead>
						<tr>
							<th>{t("Medicine")}</th>
							<th>{t("Dosage")}</th>
							<th className="cp-col-qty">{t("Qty")}</th>
							<th>{t("Duration")}</th>
							<th>{t("Instruction")}</th>
						</tr>
					</thead>
					<tbody>
						{prescriptions.map((item, index) => (
							<tr key={item._id || index}>
								<td>
									<div className="cp-item-title">{item.medication_name}</div>
									{item.salt_composition && (
										<div className="cp-item-sub">{item.salt_composition}</div>
									)}
									{item.product_form && (
										<span className="cp-form-tag">{item.product_form}</span>
									)}
								</td>
								<td>{item.dosage_name || item.dosage || "-"}</td>
								<td>{item.qty ?? "-"}</td>
								<td>{item.duration || "-"}</td>
								<td className="text-muted">{item.instruction || "-"}</td>
							</tr>
						))}
					</tbody>
				</Table>
			</div>
		</div>
	);
};

export const SuggestedInvestigationsBlock = ({ suggestions, t }) => {
	if (!suggestions?.length) return null;

	return (
		<div className="cp-prescribed-block cp-suggested-block">
			<div className="cp-block-label">{t("Suggested Investigations")}</div>
			<p className="cp-block-hint">
				{t("Doctor suggestions — not billed until added via Add Investigation.")}
			</p>
			<ul className="cp-suggested-list">
				{suggestions.map((item) => (
					<li key={item.id || item.test_id} className="cp-suggested-item">
						<div className="cp-line-name">
							<span>{item.test_name || item.name}</span>
							<SourceBadge variant="suggested">{t("Suggested")}</SourceBadge>
						</div>
						{item.suggestion_reason ? (
							<div className="cp-item-sub">{item.suggestion_reason}</div>
						) : null}
					</li>
				))}
			</ul>
		</div>
	);
};

export const PharmacyPurchasesBlock = ({
	pharmacyItems,
	displayCurrency,
	pharmacyCurrency,
	convertCurrency,
	getCurrencySymbol,
	t,
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	discountType = "Percentage",
	discountValue = 0,
	showPosTax = true,
}) => {
	if (!pharmacyItems?.length) return null;

	const identityConvert =
		typeof convertCurrency === "function"
			? convertCurrency
			: (amount) => amount;

	return (
		<div className="cp-purchases-block">
			<div className="cp-block-label">{t("Pharmacy Purchases")}</div>
			<div className="billing-payment-modal">
				<div className="billing-table-wrap">
					<BillingPharmacyMedicinesTable
						t={t}
						cart={[]}
						priorPharmacyItems={pharmacyItems}
						itemTaxes={{}}
						totals={{}}
						discountType={discountType}
						discountValue={discountValue}
						posTaxType={posTaxType}
						pharmacyStateCode={pharmacyStateCode}
						patientStateCode={patientStateCode}
						displayCurrency={displayCurrency}
						pharmacyCurrency={pharmacyCurrency || displayCurrency}
						convertCurrency={identityConvert}
						getCurrencySymbol={getCurrencySymbol}
						showPosTax={showPosTax}
					/>
				</div>
			</div>
		</div>
	);
};

const CollectPaymentBillingPanel = ({
	invoice,
	loading,
	billingFlags,
	displayCurrency,
	defaultCurrency,
	posTaxType,
	pharmacyStateCode,
	patientStateCode,
	serviceItems,
	investigationItems,
	suggestedInvestigations = [],
	pharmacyItems,
	servicesBillingTotals,
	investigationsBillingTotals,
	billingSummary,
	servicesDiscountType,
	servicesDiscountValue,
	servicesTaxConfig,
	investigationsDiscountType,
	investigationsDiscountValue,
	investigationsTaxConfig,
	servicesDiscountLocked = false,
	servicesTaxLocked = false,
	investigationsDiscountLocked = false,
	investigationsTaxLocked = false,
	generalMasterList,
	labMasterList,
	sectionOpen,
	toggleSection,
	updateServiceAmount,
	updateServiceQuantity,
	updateInvestigationAmount,
	updateInvestigationQuantity,
	removeServiceItem,
	removeInvestigation,
	addServiceRow,
	addInvestigationRow,
	selectServiceForRow,
	selectInvestigationForRow,
	updateServicesDiscount,
	updateInvestigationsDiscount,
	updateServicesTaxConfig,
	updateInvestigationsTaxConfig,
}) => {
	const { t } = useTranslation();
	const { getCurrencySymbol, allowedCurrencies, convertCurrency } = useCurrency();
	const disableScrollProps = useDisableScrollOnFocus();

	const prescriptions = invoice?.prescriptions || [];
	const hasPharmacyPurchases = pharmacyItems.length > 0;
	const showMedicinesSection = prescriptions.length > 0 || hasPharmacyPurchases;

	const showLabSection = billingFlags?.showLabSection === true;
	const showServicesTax = billingFlags?.showServicesTax === true;
	const showLabTax = billingFlags?.showLabTax === true;

	const getServiceItemValue = useCallback(
		(item) => item?.service_id || item?._id || item?.id,
		[]
	);
	const getServiceSearchText = useCallback(
		(item) => item?.service_name || item?.name || "",
		[]
	);
	const formatServiceOptionLabel = useCallback(
		(item) =>
			formatMasterOptionLabel(
				item?.service_name || item?.name,
				item?.price ?? item?.charges ?? item?.amount ?? 0,
				item?.currency,
				getCurrencySymbol,
				defaultCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, defaultCurrency, getCurrencySymbol]
	);

	const getInvestigationItemValue = useCallback(
		(item) => item?.investigation_id || item?._id || item?.id || item?.test_id,
		[]
	);
	const getInvestigationSearchText = useCallback(
		(item) => item?.test_name || item?.name || "",
		[]
	);
	const formatInvestigationOptionLabel = useCallback(
		(item) =>
			formatMasterOptionLabel(
				item?.test_name || item?.name,
				item?.price ?? item?.charges ?? item?.amount ?? 0,
				item?.currency,
				getCurrencySymbol,
				defaultCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, defaultCurrency, getCurrencySymbol]
	);

	const usedServiceIds = new Set(
		serviceItems.filter((item) => item.service_id).map((item) => String(item.service_id))
	);

	const amountInputProps = {
		displayCurrency,
		convertCurrency,
		pharmacyCurrency: defaultCurrency,
		allowedCurrencies,
		disableScrollProps,
	};

	const isEditableService = (item) => !item.isConsultation && Boolean(item.isNewRow);
	const isLockedService = (item) => !isEditableService(item);
	const isEditableInvestigation = (item) => Boolean(item.isNewRow);
	const isLockedInvestigation = (item) => !isEditableInvestigation(item);

	if (loading) {
		return <div className="cp-loading">{t("Loading billing details...")}</div>;
	}

	return (
		<div className="cp-billing-panel">
			<SectionBlock
				accent="services"
				icon="fa-stethoscope"
				title={t("Clinic Consultation & Services")}
				total={billingSummary.services_total ?? 0}
				currency={displayCurrency}
				getCurrencySymbol={getCurrencySymbol}
				isOpen={sectionOpen.services}
				onToggle={() => toggleSection("services")}
				itemCount={serviceItems.length}
				footer={
					<>
						<SectionDivider title={t("Discount & Tax")} />
						<div className="cp-adjustments-body">
							<BillingServicesAdjustments
								t={t}
								servicesBilling={servicesBillingTotals}
								servicesTaxConfig={servicesTaxConfig}
								servicesDiscountType={servicesDiscountType}
								servicesDiscountValue={servicesDiscountValue}
								onDiscountTypeChange={(type) => updateServicesDiscount({ discountType: type })}
								onDiscountValueChange={(value) =>
									updateServicesDiscount({ discountValue: value })
								}
								onTaxConfigChange={updateServicesTaxConfig}
								displayCurrency={displayCurrency}
								getCurrencySymbol={getCurrencySymbol}
								posTaxType={posTaxType}
								pharmacyStateCode={pharmacyStateCode}
								patientStateCode={patientStateCode}
								disableScrollProps={disableScrollProps}
								showTax={showServicesTax}
								disabledDiscount={servicesDiscountLocked}
								disabledTax={servicesTaxLocked}
							/>
						</div>
					</>
				}
			>
				<div className="table-responsive cp-table-wrap">
					<Table size="sm" className="cp-table mb-0">
						<thead>
							<tr>
								<th>{t("Service")}</th>
								<th className="cp-col-qty">{t("Qty")}</th>
								<th className="cp-col-amount">{t("Price")}</th>
								<th className="cp-col-action" />
							</tr>
						</thead>
						<tbody>
							{serviceItems.map((item) => (
								<tr key={item.id}>
									<td>
										{isLockedService(item) ? (
											<div className="cp-line-name">
												<span>{item.service_name}</span>
												{item.isConsultation ? (
													<SourceBadge variant="consultation">{t("Consultation")}</SourceBadge>
												) : (
													<SourceBadge variant="prescribed">{t("Prescribed")}</SourceBadge>
												)}
											</div>
										) : (
											<BillingMasterSelect
												items={generalMasterList}
												value={item.service_id || ""}
												selectedLabel={item.service_name}
												getItemValue={getServiceItemValue}
												getItemSearchText={getServiceSearchText}
												formatItemLabel={formatServiceOptionLabel}
												isItemDisabled={(masterItem) => {
													const id = String(getServiceItemValue(masterItem) || "");
													return (
														usedServiceIds.has(id) &&
														String(item.service_id) !== id
													);
												}}
												onChange={(nextId) => selectServiceForRow(item.id, nextId)}
												placeholder={t("Select service")}
												noOptionsMessage={(inputValue) =>
													!inputValue
														? t("Type to search services...")
														: t("No services found")
												}
											/>
										)}
									</td>
									<td>
										<Form.Control
											type="number"
											size="sm"
											min={1}
											className="cp-qty-input"
											value={item.quantity ?? 1}
											disabled={isLockedService(item) || !item.service_id}
											onChange={(e) =>
												updateServiceQuantity(
													item.id,
													Math.max(1, parseInt(e.target.value, 10) || 1)
												)
											}
											{...disableScrollProps}
										/>
									</td>
									<td className="text-end">
										<InputGroup size="sm" className="cp-amount-group">
											<InputGroup.Text>{getCurrencySymbol(displayCurrency)}</InputGroup.Text>
											<LineAmountInput
												item={item}
												updateFn={updateServiceAmount}
												disabled={isLockedService(item) || !item.service_id}
												{...amountInputProps}
											/>
										</InputGroup>
									</td>
									<td className="cp-col-action">
										{isEditableService(item) && (
											<button
												type="button"
												className="cp-remove-btn"
												onClick={() => removeServiceItem(item.id)}
												aria-label={t("Remove service")}
											>
												<i className="fa fa-trash" />
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</div>
				<Button variant="outline-primary" size="sm" className="cp-add-btn" onClick={addServiceRow}>
					<i className="fa fa-plus me-1" />
					{t("Add Service")}
				</Button>
			</SectionBlock>

			{showLabSection && (
			<SectionBlock
				accent="investigations"
				icon="fa-flask"
				title={t("Lab Investigations")}
				total={billingSummary.investigation_total ?? 0}
				currency={displayCurrency}
				getCurrencySymbol={getCurrencySymbol}
				isOpen={sectionOpen.investigations}
				onToggle={() => toggleSection("investigations")}
				itemCount={investigationItems.length}
				footer={
					<>
						<SectionDivider title={t("Discount & Tax")} />
						<div className="cp-adjustments-body">
							<BillingInvestigationsAdjustments
								t={t}
								investigationsBilling={investigationsBillingTotals}
								investigationsTaxConfig={investigationsTaxConfig}
								investigationsDiscountType={investigationsDiscountType}
								investigationsDiscountValue={investigationsDiscountValue}
								onDiscountTypeChange={(type) =>
									updateInvestigationsDiscount({ discountType: type })
								}
								onDiscountValueChange={(value) =>
									updateInvestigationsDiscount({ discountValue: value })
								}
								onTaxConfigChange={updateInvestigationsTaxConfig}
								displayCurrency={displayCurrency}
								getCurrencySymbol={getCurrencySymbol}
								posTaxType={posTaxType}
								pharmacyStateCode={pharmacyStateCode}
								patientStateCode={patientStateCode}
								disableScrollProps={disableScrollProps}
								showTax={showLabTax}
								disabledDiscount={investigationsDiscountLocked}
								disabledTax={investigationsTaxLocked}
							/>
						</div>
					</>
				}
			>
				<SuggestedInvestigationsBlock suggestions={suggestedInvestigations} t={t} />
				{suggestedInvestigations.length > 0 && investigationItems.length > 0 && (
					<div className="cp-section-divider" />
				)}
				<div className="table-responsive cp-table-wrap">
					<Table size="sm" className="cp-table mb-0">
						<thead>
							<tr>
								<th>{t("Test")}</th>
								<th className="cp-col-qty">{t("Qty")}</th>
								<th className="cp-col-amount">{t("Price")}</th>
								<th className="cp-col-action" />
							</tr>
						</thead>
						<tbody>
							{investigationItems.length > 0 ? (
								investigationItems.map((item) => (
									<tr key={item.id}>
										<td>
											{isLockedInvestigation(item) ? (
												<div className="cp-line-name">
													<span>{item.test_name}</span>
													<SourceBadge variant="prescribed">{t("Prescribed")}</SourceBadge>
												</div>
											) : (
												<BillingMasterSelect
													items={labMasterList}
													value={item.test_id || ""}
													selectedLabel={item.test_name}
													getItemValue={getInvestigationItemValue}
													getItemSearchText={getInvestigationSearchText}
													formatItemLabel={formatInvestigationOptionLabel}
													onChange={(nextId) =>
														selectInvestigationForRow(item.id, nextId)
													}
													placeholder={t("Select test")}
													noOptionsMessage={(inputValue) =>
														!inputValue
															? t("Type to search investigations...")
															: t("No investigations found")
													}
												/>
											)}
										</td>
										<td>
											<Form.Control
												type="number"
												size="sm"
												min={1}
												className="cp-qty-input"
												value={item.quantity ?? 1}
												disabled={isLockedInvestigation(item) || !item.test_id}
												onChange={(e) =>
													updateInvestigationQuantity(
														item.id,
														Math.max(1, parseInt(e.target.value, 10) || 1)
													)
												}
												{...disableScrollProps}
											/>
										</td>
										<td className="text-end">
											<InputGroup size="sm" className="cp-amount-group">
												<InputGroup.Text>
													{getCurrencySymbol(displayCurrency)}
												</InputGroup.Text>
												<LineAmountInput
													item={item}
													updateFn={updateInvestigationAmount}
													disabled={isLockedInvestigation(item) || !item.test_id}
													{...amountInputProps}
												/>
											</InputGroup>
										</td>
										<td className="cp-col-action">
											{isEditableInvestigation(item) && (
												<button
													type="button"
													className="cp-remove-btn"
													onClick={() => removeInvestigation(item.id)}
													aria-label={t("Remove investigation")}
												>
													<i className="fa fa-trash" />
												</button>
											)}
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={4} className="text-center text-muted py-3">
										{t("No lab tests added")}
									</td>
								</tr>
							)}
						</tbody>
					</Table>
				</div>
				<Button
					variant="outline-primary"
					size="sm"
					className="cp-add-btn"
					onClick={addInvestigationRow}
				>
					<i className="fa fa-plus me-1" />
					{t("Add Investigation")}
				</Button>
			</SectionBlock>
			)}

			{showMedicinesSection && (
				<SectionBlock
					accent="medicines"
					icon="fa-medkit"
					title={t("Pharmacy")}
					total={hasPharmacyPurchases ? billingSummary.pharmacy_total ?? 0 : null}
					showTotal={hasPharmacyPurchases}
					currency={displayCurrency}
					getCurrencySymbol={getCurrencySymbol}
					isOpen={sectionOpen.medicines}
					onToggle={() => toggleSection("medicines")}
					itemCount={hasPharmacyPurchases ? pharmacyItems.length : prescriptions.length}
				>
					{/* Show doctor prescription when not yet dispensed/billed as pharmacy */}
					{!hasPharmacyPurchases && (
						<PrescribedMedicinesBlock prescriptions={prescriptions} t={t} />
					)}
					{hasPharmacyPurchases && prescriptions.length > 0 && (
						<>
							<PrescribedMedicinesBlock prescriptions={prescriptions} t={t} />
							<div className="cp-section-divider" />
						</>
					)}
					<PharmacyPurchasesBlock
						pharmacyItems={pharmacyItems}
						displayCurrency={displayCurrency}
						pharmacyCurrency={defaultCurrency}
						convertCurrency={convertCurrency}
						getCurrencySymbol={getCurrencySymbol}
						t={t}
						posTaxType={posTaxType}
						pharmacyStateCode={pharmacyStateCode}
						patientStateCode={patientStateCode}
						discountType={billingSummary?.pharmacy_discount_type || "Percentage"}
						discountValue={billingSummary?.pharmacy_discount_value ?? 0}
					/>
				</SectionBlock>
			)}
		</div>
	);
};

export default CollectPaymentBillingPanel;
